import { useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { answerPropType } from '@aytm/app/stats/page/question-views/components/lib/prop-types'
import classNames from 'classnames'

import HeadIcon from '@aytm/app/stats/page/question-views/cjc/components/head/HeadIcon'
import HeadOptions from '@aytm/app/stats/page/question-views/cjc/components/head/HeadOptions'
import Persona from '@aytm/app/stats/page/question-views/cjd/components/head/Persona'
import TransitionPanel from '@aytm/app/stats/components/transition-panel/TransitionPanel'
import { pressable } from '@aytm/app/stats/components/lib/pressable'

import style from '@aytm/app/stats/page/question-views/cjc/components/head/styles/Head.module.scss'

const EXPORT_OPTIONS = [
  { key: 'conjoint', text: 'Download Utility Scores (CSV)', fileType: 'csv', exportType: 'conjoint' },
  { key: 'conjointPkgs', text: 'Download Ranked List of Packages (CSV)', fileType: 'csv', exportType: 'conjointPkgs' },
  {
    key: 'stacked',
    text: 'Stacked choice data (CSV)',
    fileType: 'csv',
    exportType: 'stacked',
    exportMethod: 'conjoint'
  },
  { key: 'conjointDesign', text: 'Download Design Sheet by aytm (CSV)', fileType: 'csv', exportType: 'conjointDesign' }
]

// Which personas are selectable: user 0 (all) is always present; users 1..clusterN are disabled
// when their cluster has no responses. Mirrors the deep head template.
const usePersonas = answer =>
  useMemo(() => {
    if (!answer || 'loading' === answer) return []

    const clusterN = undefined !== answer.clusterN ? answer.clusterN : 0

    return Array.from({ length: clusterN }, (_, i) => {
      const cluster = (answer.clusters || [])[i] || {}
      return { index: i + 1, disabled: !Object.keys(cluster).length || !cluster.amount }
    })
  }, [answer])

// The Conjoint Segmentation head: the badge and options row both types draw, plus what only this
// type has - the cluster persona selector and the persona card it toggles.
const Head = ({
  traits,
  questions,
  answer,
  persona,
  showPersonaCard,
  onSelectPersona,
  onScrollToQuestion,
  ...props
}) => {
  const personas = usePersonas(answer)
  const isLoading = 'loading' === answer

  // Persona-card visibility is head-local: nothing outside the head reads it and it is not
  // persisted to report params.
  const [personaVisible, setPersonaVisible] = useState(false)
  const [personaToggled, setPersonaToggled] = useState(false)

  // picking a persona shows its card straight away, but only until the researcher has had a say --
  // once they have worked the toggle themselves, a later pick leaves their choice alone
  const onSelect = (event, index) => {
    event.preventDefault()
    if (!personaToggled && 0 < index) setPersonaVisible(true)
    onSelectPersona(index)
  }

  const onToggle = event => {
    event.preventDefault()
    setPersonaToggled(true)
    setPersonaVisible(visible => !visible)
  }

  return (
    <>
      <div className={style.head}>
        <div className={style.icon}>
          <HeadIcon />
          <span>
            Conjoint
            <br />
            Segmentation<sup>SM</sup>
          </span>
        </div>

        <div className={style.users}>
          <span
            className={classNames(style.user, { [style.active]: 0 === persona })}
            aria-label="All respondents"
            aria-pressed={0 === persona}
            {...pressable(event => onSelect(event, 0))}
          >
            <i className="icon-aytmfd-users" />
          </span>
          {personas.map(({ index, disabled }) =>
            disabled ? (
              <span key={index} className={classNames(style.user, style.disabled)}>
                <i className="icon-aytmfd-singleuser" />
                <span>{index}</span>
              </span>
            ) : (
              <span
                key={index}
                className={classNames(style.user, { [style.active]: index === persona })}
                aria-label={`Persona ${index}`}
                aria-pressed={index === persona}
                {...pressable(event => onSelect(event, index))}
              >
                <i className="icon-aytmfd-singleuser" />
                <span>{index}</span>
              </span>
            )
          )}
        </div>

        {!isLoading && 0 < persona ? (
          <div className={style.settings}>
            <span {...pressable(onToggle)}>{personaVisible ? 'Hide Personas' : 'Show Personas'}</span>
          </div>
        ) : null}

        <HeadOptions {...props} questionType="cjd" exportOptions={EXPORT_OPTIONS} />
      </div>

      {showPersonaCard ? (
        <TransitionPanel in={personaVisible}>
          {status =>
            'exited' === status ? null : (
              <Persona
                traits={traits}
                questions={questions}
                answer={answer}
                persona={persona}
                visible
                onScrollToQuestion={onScrollToQuestion}
              />
            )
          }
        </TransitionPanel>
      ) : null}
    </>
  )
}

Head.displayName = 'ConjointSegmentationHead'

Head.propTypes = {
  traits: PropTypes.array,
  questions: PropTypes.array,
  answer: answerPropType,
  persona: PropTypes.number,
  showPersonaCard: PropTypes.bool,
  isLite: PropTypes.bool,
  isSummaryChartView: PropTypes.bool,
  isAwaitingFullSlice: PropTypes.bool,
  showExport: PropTypes.bool,
  fullViewOnly: PropTypes.bool,
  onExport: PropTypes.func,
  onSelectPersona: PropTypes.func,
  onScrollToQuestion: PropTypes.func,
  onShowBar: PropTypes.func,
  onShowVisual: PropTypes.func
}

export default Head
